import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  BookOpenIcon,
  VideoCameraIcon,
  ChatBubbleLeftRightIcon,
  RocketLaunchIcon,
  LinkIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  CommandLineIcon,
  ShieldCheckIcon,
  BoltIcon,
  DocumentTextIcon,
  PlayCircleIcon,
  ClockIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import BrandLogo from '../components/shared/BrandLogo';
import { BRAND_HERO_GRADIENT } from '../components/shared/SectionTitle';

// ─── Guide Content ────────────────────────────────────────────────────────────

const GUIDE_CONTENT = {
  'Quick Start Guide': {
    icon: RocketLaunchIcon,
    color: 'text-fuchsia-600',
    bg: 'bg-fuchsia-50 dark:bg-fuchsia-900/20',
    readTime: '5 min read',
    sections: [
      {
        title: 'Step 1 — Create Your Account',
        body: 'Sign up at OmniConnX.com using your email address. You will receive a verification email; click the link to activate your account. Passwords must be at least 8 characters and include a number and a special character.',
        tip: 'Use a Google or Apple account for faster sign-up.',
      },
      {
        title: 'Step 2 — Run the Onboarding Wizard',
        body: 'On first login, the onboarding wizard walks you through selecting your creator platforms, setting content goals (audience growth, revenue, engagement), and configuring notification preferences. You can revisit any step from Settings at any time.',
      },
      {
        title: 'Step 3 — Connect Your First Platform',
        body: 'Connecting is one click — no developer setup or authorization codes. You can connect a platform from two places:\n\n1. Settings → Integrations — find the platform and click Connect.\n2. The Analytics dashboard — in the Platforms card, click Connect under the platform.\n\nEither way opens the platform\'s secure OAuth consent screen; approve access and you\'re done. Your token is stored encrypted and never exposed, and data starts syncing immediately. To disconnect, click Disconnect in the same two places.',
        tip: 'Connect YouTube first — it has the richest analytics dataset.',
      },
      {
        title: 'Step 4 — Explore the Analytics Dashboard',
        body: 'Go to Analytics. Your dashboard shows Total Views, Likes, Comments, and Subscriber counts aggregated across all connected platforms. Use the Quick Filter (7 / 30 / 90 days) or the date-range picker for custom periods. Platform filters let you isolate metrics per network.',
      },
      {
        title: 'Step 5 — Set Up Health Alerts',
        body: 'The Health Monitor tab uses AI anomaly detection across all your metrics. The system flags sudden drops in reach, engagement spikes, shadow-ban signals, and token expiry. Alerts are colour-coded by severity (LOW → CRITICAL) and can also be sent to your email.',
      },
      {
        title: 'Step 6 — Add Revenue Tracking',
        body: 'Go to Revenue. Add revenue entries manually or let the system pull earnings from connected platforms (YouTube AdSense, TikTok Creator Fund, etc.). Link your Stripe account to enable automatic reconciliation — OmniConnX matches platform payments to Stripe charges and flags any discrepancies.',
      },
    ],
  },
  'Platform Integration Guide': {
    icon: LinkIcon,
    color: 'text-blue-600',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    readTime: '10 min read',
    sections: [
      {
        title: 'How OAuth 2.0 Works in OmniConnX',
        body: 'OmniConnX uses the OAuth 2.0 Authorization Code flow for all platform connections. You never share your password — you approve access on the platform\'s own consent screen, and OmniConnX exchanges the result for access and refresh tokens. Tokens are stored AES-256 encrypted and rotate automatically.\n\nConnecting is one click, from two places in the app: Settings → Integrations, or the Platforms card on the Analytics dashboard. Both open the same consent flow, and Disconnect is available in both. The per-platform notes below are reference for self-hosted or advanced deployments — on OmniConnX you don\'t need a developer app or authorization code.',
      },
      {
        title: 'YouTube — YouTube Data API v3',
        body: 'Prerequisites: A Google account with a YouTube channel. Required scopes: youtube.readonly, yt-analytics.readonly.\n\n1. Open Google Cloud Console → New Project → Enable YouTube Data API v3.\n2. Credentials → Create OAuth 2.0 Client ID (Web application), add redirect URI.\n3. Use the OAuth Playground to generate an authorization code.\n4. Paste the code into Settings → Integrations → Connect YouTube.\n\nData collected: subscriber count, view count, watch time, likes, comments, video-level breakdown.',
        tip: 'Quota resets daily at midnight PT. OmniConnX caches responses to stay within limits.',
      },
      {
        title: 'TikTok — Login Kit + Research API',
        body: '1. Go to TikTok for Developers → Create App → Enable Login Kit.\n2. Add your redirect URI and copy the Client Key.\n3. Construct the auth URL and open it in a browser.\n4. Copy the code= parameter from the redirect and paste it in Settings → Integrations.\n\nData collected: video views, likes, shares, comments, follower count, profile analytics.',
      },
      {
        title: 'Instagram — Meta Basic Display API',
        body: '1. Go to Meta for Developers → Create App → Add Instagram Basic Display.\n2. Add test user, set redirect URI.\n3. Open the auth URL, authorize, copy the code= parameter.\n4. Paste into Settings → Integrations → Connect Instagram.\n\nData collected: media reach, impressions, profile views, follower demographics.',
        tip: 'Instagram Basic Display is limited to your own account. For business/creator accounts with richer metrics, use the Instagram Graph API and connect via a Facebook Page.',
      },
      {
        title: 'Twitter / X — OAuth 2.0 PKCE',
        body: '1. Go to developer.twitter.com → Create Project & App → Enable OAuth 2.0.\n2. Set callback URL, select scopes: tweet.read, users.read.\n3. Generate code_verifier + code_challenge (SHA-256).\n4. Open the auth URL, authorize, copy the code= parameter.\n5. Paste into Settings → Integrations → Connect Twitter.\n\nData collected: tweet impressions, engagements, profile visits, follower count.',
      },
      {
        title: 'Reconnecting and Token Refresh',
        body: 'Access tokens expire (typically 1–60 days depending on the platform). OmniConnX proactively refreshes tokens before they expire using refresh tokens. If a refresh fails (e.g. user revoked access), you will receive a TOKEN_EXPIRY health alert. Simply click the edit icon on the integration card and click "Refresh Token" or reconnect.',
      },
    ],
  },
  'Analytics Dashboard Overview': {
    icon: ChartBarIcon,
    color: 'text-purple-600',
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    readTime: '8 min read',
    sections: [
      {
        title: 'Stats Cards — Key Metrics at a Glance',
        body: 'The four stat cards show aggregated totals across all connected platforms for the selected date range:\n• Total Views — combined video/post view count\n• Total Likes — combined reactions/thumbs-up\n• Comments — total comment count\n• Subscribers/Followers — net change in the period\n\nEach card shows a percentage change vs. the previous equivalent period (trend arrow up/down).',
      },
      {
        title: 'Views & Engagement Chart',
        body: 'The line chart plots daily views, likes, and comments over the selected period. Hover any data point to see exact numbers. Multiple lines are colour-coded: blue = views, red = likes, green = comments. Use the Platform filter at the top to isolate one network.',
        tip: 'Spikes in the chart often correlate with viral content. Cross-reference with the Top Performing Content table below.',
      },
      {
        title: 'Engagement Rate Chart',
        body: 'Engagement rate = (likes + comments + shares) ÷ views × 100. This chart shows your daily engagement rate as a percentage. A healthy creator engagement rate is typically 3–8%. Rates below 2% may trigger a REACH_DROP health alert.',
      },
      {
        title: 'Top / Recent Content',
        body: 'Lists your highest-performing content for the selected period, ranked by views (title, platform, views, engagement rate, publish date). Click a row to open the original post. For the full per-post breakdown — every video, photo, reel, short, story, and text post, with sortable columns and format filters — open the Content Analytics page (see its own guide).',
      },
      {
        title: 'Managing Platform Connections from the Dashboard',
        body: 'The Platforms card shows a ring per network with its health score. You can connect and disconnect right here — no need to open Settings:\n• Not connected? Click Connect under the platform, approve access on the platform\'s consent screen, and it links immediately.\n• Connected? Click Disconnect to remove the stored token and stop syncing.\n\nThe same actions live in Settings → Integrations. Click a platform ring to filter the whole dashboard to that network.',
      },
      {
        title: 'Light / Dark Theme (Automatic by Time of Day)',
        body: 'By default OmniConnX matches the time of day — light from 6am to 6pm, dark from 6pm to 6am — and switches on its own as the day goes on. To override, click the theme button in the top bar: it cycles Auto → Light → Dark. Your choice is remembered; pick Auto again to return to the automatic schedule.',
      },
      {
        title: 'Filters and Date Ranges',
        body: 'Platforms filter: Select one or more platforms to isolate their data. Deselecting all shows aggregated cross-platform data.\n\nQuick filters: Last 7 / 30 / 90 days — updates all charts and tables instantly.\n\nCustom date range: Use the calendar picker to select any start and end date. Active filters are displayed as tags; click "Clear all" to reset.',
      },
      {
        title: 'Real-Time Updates via WebSocket',
        body: 'OmniConnX maintains a WebSocket connection to the server. When a background job pulls new metrics from a platform, your dashboard updates live without a page refresh. A toast notification appears in the top-right corner when live data arrives. Click Refresh to manually trigger a pull from all connected platforms.',
      },
    ],
  },
  'Revenue Reconciliation': {
    icon: CurrencyDollarIcon,
    color: 'text-green-600',
    bg: 'bg-green-50 dark:bg-green-900/20',
    readTime: '12 min read',
    sections: [
      {
        title: 'Supported Revenue Sources',
        body: 'OmniConnX tracks earnings from seven source types:\n• YouTube AdSense — ad revenue from monetised videos\n• TikTok Creator Fund — per-view payouts from TikTok\n• Instagram Badges — live stream badge purchases\n• Brand Deals — sponsored content payments\n• Patreon — recurring membership revenue\n• Merchandise — product sales\n• Other — custom sources\n\nEach entry records the source, amount, currency (default USD), revenue date, and an optional platform transaction ID.',
      },
      {
        title: 'How Automatic Reconciliation Works',
        body: 'OmniConnX runs a nightly reconciliation job (2:00 AM UTC) that:\n1. Fetches all unreconciled revenue entries for the past 90 days.\n2. Queries your connected Stripe account for charges and invoices in the same window.\n3. Matches entries by amount (within ±$0.01) and date (within 7 days).\n4. Marks matched entries as reconciled with a timestamp.\n5. Flags unmatched entries as discrepancies for your review.',
        tip: 'Connect your Stripe account in Settings → Integrations to enable automatic reconciliation.',
      },
      {
        title: 'Understanding Discrepancy Types',
        body: '• MISSING_PAYMENT — a revenue entry has no matching Stripe charge\n• AMOUNT_MISMATCH — the platform-reported amount differs from the Stripe amount by more than $0.01\n• DELAYED_PAYMENT — matching charge found but arrived outside the 7-day window\n\nAll discrepancies are listed in the Revenue → Discrepancies tab with their status: PENDING → INVESTIGATING → RESOLVED or DISMISSED.',
      },
      {
        title: 'Adding Revenue Entries Manually',
        body: '1. Navigate to Revenue → Add Entry.\n2. Select the revenue source.\n3. Enter the amount, currency, and revenue date.\n4. Optionally paste the platform transaction ID (enables more accurate auto-matching).\n5. Save — the entry enters the reconciliation queue immediately.\n\nBulk imports are supported via CSV upload (Revenue → Import).',
      },
      {
        title: 'Brand Deal Revenue',
        body: 'Brand deal payments are tracked through the Deals module. When a deal stage moves to "Payment Received", a revenue entry is created automatically with source = BRAND_DEAL and the deal ID attached. These are reconciled against Stripe transfers or manual marks.',
      },
      {
        title: 'Revenue Reports & Exports',
        body: 'Go to Revenue → Reports to generate:\n• Monthly summary (total by source)\n• Annual P&L statement (useful for tax filing)\n• Discrepancy audit log\n• CSV export of all revenue entries in a date range\n\nAll reports include the reconciliation status of each entry. PDF exports include your creator profile header for professional delivery to accountants or brand partners.',
      },
    ],
  },
  'Content Analytics': {
    icon: PlayCircleIcon,
    color: 'text-rose-600',
    bg: 'bg-rose-50 dark:bg-rose-900/20',
    readTime: '7 min read',
    sections: [
      {
        title: 'What the Content Analytics Page Shows',
        body: 'Content Analytics (in the sidebar; "Insights" on the mobile bottom bar) is your per-post performance view across every connected platform. Each row is one piece of content — video, photo, reel, short, story, or text post — with its platform, format, views, likes, comments, reach, engagement rate, and publish date. Use it to see exactly what you posted and how each item is doing, rather than platform-level totals.',
      },
      {
        title: 'Total vs 7D / 30D / 90D',
        body: 'A single control switches what the page shows:\n• Total — all of your content.\n• 7D / 30D / 90D — only content PUBLISHED in that window.\n\nSwitching the range changes the numbers and counts (including the per-format chip counts and the stat cards), not just a subset of columns. The caption under the control always says what you\'re looking at. Note: the window is based on each post\'s real publish date, which is captured on sync — after connecting or upgrading, run Sync All once so older posts get their dates.',
      },
      {
        title: 'The Published Column (and Sorting)',
        body: 'The Published column shows the exact date and time each post went live (e.g. "Mar 10, 2026, 2:30 PM"), not a vague "2 months ago". Click the Published header to sort chronologically — first click newest-first, second click oldest-first — just like the Views, Likes, and other sortable columns.',
      },
      {
        title: 'Performance by Format',
        body: 'The Performance-by-format card breaks your results down by content type (Video, Photo, Reel, Short, Story, Carousel, Text). Group it by format or by platform, and hover any bar for the exact numbers. Facebook text-only posts are tagged TEXT rather than "Unknown". This is the fastest way to see which format earns the most engagement for you.',
      },
      {
        title: 'Filters, Search, Export & Sync',
        body: 'Filter by platform and by one or more formats (the chip counts update to match), or search by title. Sync All pulls the latest metrics from every connected platform, and Export CSV downloads the current view for spreadsheets or reporting.',
      },
    ],
  },
  'Publishing Content': {
    icon: DocumentTextIcon,
    color: 'text-cyan-600',
    bg: 'bg-cyan-50 dark:bg-cyan-900/20',
    readTime: '6 min read',
    sections: [
      {
        title: 'Creating & Publishing a Post',
        body: 'On the Content page, click New Content, pick a content type (Video, Photo, Reel, Short, Story, Carousel, or Text), upload your media, add a title/caption and hashtags, choose which connected platforms to publish to, and publish. OmniConnX posts to each selected platform and tracks the result per platform.',
      },
      {
        title: 'Uploading Media (Desktop & Mobile)',
        body: 'Drag a file onto the upload area or click/tap to browse. On mobile the file picker lets you choose an existing photo or video from your library or Files — or use the camera — so you\'re never forced straight into the camera.',
      },
      {
        title: 'Publish Status & Per-Platform Progress',
        body: 'Each content row shows an overall status — Draft, Scheduled, Partial, or Published — and the Platform column shows the per-platform result inline: a check when it published, a spinner while it\'s in progress, and a red mark if it failed (hover for the error). "Partial" means it succeeded on some platforms but not all; you can retry the failed ones.',
      },
      {
        title: 'Facebook & Instagram Notes',
        body: 'Instagram publishing requires a Business or Creator account connected through your Facebook Page. Instagram processes each image or video before it goes live, so a post may take a moment to appear. Facebook photos, videos, and text posts are all supported; a published Facebook post is picked up into your analytics on the next sync.',
      },
      {
        title: 'Thumbnails',
        body: 'Image posts use the uploaded picture as their thumbnail automatically. For videos you can capture a specific frame as the thumbnail while creating the post.',
      },
    ],
  },
};

// ─── Video Tutorials ──────────────────────────────────────────────────────────

const VIDEO_TUTORIALS = [
  {
    id: 1,
    title: 'Getting Started with OmniConnX',
    description: 'A complete walkthrough of setting up your account, running the onboarding wizard, and connecting your first platform.',
    duration: '5:30',
    category: 'getting-started',
    thumbnail: null,
    level: 'Beginner',
  },
  {
    id: 2,
    title: 'Connecting Your YouTube Channel',
    description: 'Step-by-step OAuth 2.0 setup for YouTube: Google Cloud Console, API key, and authorisation code.',
    duration: '3:45',
    category: 'platforms',
    thumbnail: null,
    level: 'Beginner',
  },
  {
    id: 3,
    title: 'Understanding Your Analytics Dashboard',
    description: 'Deep dive into the stats cards, engagement charts, top content table, and how to use filters effectively.',
    duration: '7:20',
    category: 'analytics',
    thumbnail: null,
    level: 'Intermediate',
  },
  {
    id: 4,
    title: 'Setting Up Health Alerts & Anomaly Detection',
    description: 'How the AI anomaly detection works, how to customise alert thresholds, and what to do when an alert fires.',
    duration: '4:15',
    category: 'analytics',
    thumbnail: null,
    level: 'Intermediate',
  },
  {
    id: 5,
    title: 'Revenue Tracking & Stripe Reconciliation',
    description: 'Connecting Stripe, adding revenue entries, understanding discrepancy types, and generating tax-ready reports.',
    duration: '8:30',
    category: 'billing',
    thumbnail: null,
    level: 'Advanced',
  },
  {
    id: 6,
    title: 'Managing Brand Deals End-to-End',
    description: 'Creating deal pipelines, tracking milestones, logging payments, and reconciling brand deal revenue.',
    duration: '6:00',
    category: 'getting-started',
    thumbnail: null,
    level: 'Intermediate',
  },
];

// ─── Documentation Section ────────────────────────────────────────────────────

const DOC_TABS = [
  {
    id: 'overview',
    label: 'Overview',
    icon: BookOpenIcon,
    content: [
      {
        heading: 'What is OmniConnX?',
        body: 'OmniConnX is a unified creator intelligence platform. It aggregates analytics from YouTube, TikTok, Instagram, and Twitter into a single dashboard, monitors account health with AI-powered anomaly detection, tracks revenue from all sources, and reconciles earnings with Stripe — all in one place.',
      },
      {
        heading: 'Architecture',
        body: 'The backend is a Spring Boot 3.x application with a PostgreSQL database, Kafka event streaming, Redis caching, and Keycloak for authentication. The frontend is a React 18 SPA with Redux for state management and Tailwind CSS for styling. All communication uses HTTPS with JWT Bearer tokens.',
      },
      {
        heading: 'Key Concepts',
        list: [
          'Platform Connection — An OAuth token pair linking your OmniConnX account to a social platform.',
          'Metrics Raw — Raw data points collected from platform APIs (views, likes, subscribers, etc.).',
          'Health Score — A composite score (0–100) reflecting account health, updated after each metrics pull.',
          'Health Alert — A notification raised when a metric deviates significantly from its baseline.',
          'Revenue Entry — A record of income from any source, awaiting or completed reconciliation.',
          'Discrepancy — A mismatch between a revenue entry and the corresponding Stripe transaction.',
        ],
      },
    ],
  },
  {
    id: 'api',
    label: 'API Reference',
    icon: CommandLineIcon,
    content: [
      {
        heading: 'Base URL',
        body: 'All API requests are made to: https://api.omniconnx.com (production) or http://localhost:8080 (local development).',
        code: 'Authorization: Bearer <JWT_TOKEN>',
      },
      {
        heading: 'Authentication Endpoints',
        list: [
          'POST /api/v1/auth/login — Login with email + password. Returns access_token and refresh_token.',
          'POST /api/v1/auth/register — Register a new account.',
          'POST /api/v1/auth/refresh — Exchange a refresh_token for a new access_token.',
          'POST /api/v1/auth/logout — Invalidate the current session.',
        ],
      },
      {
        heading: 'Platform Endpoints',
        list: [
          'GET /api/v1/platforms/connections — List all connected platforms for the current user.',
          'POST /api/v1/platforms/connect — Connect a platform using an OAuth authorization code.',
          'DELETE /api/v1/platforms/disconnect/{id} — Disconnect a platform by connection ID.',
          'POST /api/v1/platforms/refresh/{id} — Force-refresh the OAuth token for a connection.',
        ],
      },
      {
        heading: 'Analytics Endpoints',
        list: [
          'GET /api/v1/analytics/dashboard?startDate=&endDate= — Aggregated dashboard metrics.',
          'GET /api/v1/analytics/metrics?platform=&startDate=&endDate= — Platform-specific raw metrics.',
          'POST /api/v1/analytics/pull/{platform} — Trigger an on-demand metrics pull from a platform.',
          'GET /api/v1/analytics/growth?platform=&days= — Growth rate analytics over N days.',
        ],
      },
      {
        heading: 'Revenue Endpoints',
        list: [
          'GET /api/v1/revenue — List all revenue entries for the current user.',
          'POST /api/v1/revenue — Create a new revenue entry.',
          'GET /api/v1/revenue/reconciliation/summary — Reconciliation summary for the current period.',
          'GET /api/v1/revenue/discrepancies — List all unresolved discrepancies.',
        ],
      },
    ],
  },
  {
    id: 'auth',
    label: 'Authentication',
    icon: ShieldCheckIcon,
    content: [
      {
        heading: 'Token Format',
        body: 'OmniConnX uses JWT Bearer tokens. Include the token in every authenticated request header.',
        code: 'Authorization: Bearer eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9...',
      },
      {
        heading: 'Token Lifecycle',
        list: [
          'Access tokens expire after 15 minutes.',
          'Refresh tokens expire after 30 days.',
          'Use POST /api/v1/auth/refresh with your refresh_token to obtain a new access_token.',
          'The frontend SDK handles token refresh automatically before each request.',
        ],
      },
      {
        heading: 'Security Notes',
        list: [
          'Never embed access tokens in URLs or logs.',
          'Store refresh tokens in httpOnly cookies or encrypted storage only.',
          'Platform OAuth tokens are stored AES-256 encrypted server-side.',
          'OmniConnX never stores or transmits your social platform passwords.',
          'All data is encrypted in transit (TLS 1.3) and at rest (AES-256).',
        ],
      },
    ],
  },
  {
    id: 'webhooks',
    label: 'Webhooks',
    icon: BoltIcon,
    content: [
      {
        heading: 'Overview',
        body: 'OmniConnX can deliver real-time event notifications to your server via HTTP POST webhooks. Configure webhook endpoints in Settings → Developer → Webhooks.',
      },
      {
        heading: 'Available Events',
        list: [
          'metrics.pulled — Fired when a platform metrics pull completes.',
          'health.alert.created — Fired when a new health alert is raised.',
          'health.score.updated — Fired when a health score changes.',
          'revenue.entry.created — Fired when a new revenue entry is added.',
          'revenue.reconciled — Fired when an entry is successfully reconciled.',
          'revenue.discrepancy.detected — Fired when a payment discrepancy is found.',
          'platform.token.expired — Fired when a platform OAuth token expires.',
        ],
      },
      {
        heading: 'Payload Example',
        body: 'All webhook payloads are JSON with a common envelope structure.',
        code: `{
  "event": "health.alert.created",
  "timestamp": "2026-02-17T10:30:00Z",
  "data": {
    "userId": "uuid",
    "platform": "YOUTUBE",
    "alertType": "REACH_DROP",
    "severity": "HIGH",
    "message": "Reach dropped 42% vs 7-day average"
  }
}`,
      },
      {
        heading: 'Signature Verification',
        body: 'Each webhook request includes an X-OmniConnX-Signature header containing an HMAC-SHA256 signature of the raw request body signed with your webhook secret. Always verify this signature before processing events.',
      },
    ],
  },
  {
    id: 'limits',
    label: 'Rate Limits',
    icon: ClockIcon,
    content: [
      {
        heading: 'API Rate Limits',
        list: [
          'Standard plan: 100 requests / minute per user.',
          'Pro plan: 500 requests / minute per user.',
          'Enterprise: Custom limits — contact sales.',
          'Exceeded limits return HTTP 429 Too Many Requests with a Retry-After header.',
        ],
      },
      {
        heading: 'Platform Pull Frequency',
        list: [
          'Automatic metrics pulls: every 6 hours per platform.',
          'Manual pulls (Refresh button): up to 10 per hour per platform.',
          'YouTube quota: 10,000 units/day (OmniConnX caches aggressively to stay within quota).',
          'TikTok Research API: 100 calls/day (shared across all users — pulls are batched).',
        ],
      },
      {
        heading: 'Webhook Delivery',
        list: [
          'Webhooks are delivered with at-least-once semantics.',
          'Retry policy: 5 attempts with exponential backoff (1s, 2s, 4s, 8s, 16s).',
          'Failed deliveries after all retries are logged in Settings → Developer → Webhook Logs.',
          'Endpoint timeouts: 10 seconds per request.',
        ],
      },
    ],
  },
];

// ─── Guide Modal ──────────────────────────────────────────────────────────────

const GuideModal = ({ guide, onClose }) => {
  const content = GUIDE_CONTENT[guide.title];
  if (!content) return null;
  const Icon = content.icon;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-dark-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
      >
        {/* Modal Header */}
        <div className={`flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-700 ${content.bg} rounded-t-2xl`}>
          <div className="flex items-center gap-3">
            <Icon className={`w-7 h-7 ${content.color}`} />
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{guide.title}</h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">{content.readTime}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-colors">
            <XMarkIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="overflow-y-auto flex-1 p-6 space-y-8">
          {content.sections.map((section, i) => (
            <div key={i}>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                <CheckCircleIcon className={`w-5 h-5 ${content.color} flex-shrink-0`} />
                {section.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 whitespace-pre-line pl-7 text-sm leading-relaxed">
                {section.body}
              </p>
              {section.tip && (
                <div className="mt-3 ml-7 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    <span className="font-semibold">Tip: </span>{section.tip}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const HelpPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeGuide, setActiveGuide] = useState(null);
  const [activeDocTab, setActiveDocTab] = useState('overview');
  const [videoCategory, setVideoCategory] = useState('all');
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const categories = [
    { id: 'all', name: 'All Topics', icon: BookOpenIcon },
    { id: 'getting-started', name: 'Getting Started', icon: RocketLaunchIcon },
    { id: 'platforms', name: 'Platform Connections', icon: LinkIcon },
    { id: 'analytics', name: 'Analytics', icon: ChartBarIcon },
    { id: 'billing', name: 'Billing', icon: CurrencyDollarIcon },
    { id: 'troubleshooting', name: 'Troubleshooting', icon: WrenchScrewdriverIcon },
  ];

  const faqs = [
    { category: 'getting-started', question: 'How do I get started with OmniConnX?', answer: 'Sign up for a free account, connect your platforms, and start tracking your analytics. Our onboarding wizard will guide you through the setup process in under 5 minutes.' },
    { category: 'getting-started', question: 'What platforms does OmniConnX support?', answer: 'We currently support YouTube, TikTok, Instagram, and Twitter. More platforms including LinkedIn, Pinterest, and Twitch are on the roadmap for Q2 2026.' },
    { category: 'platforms', question: 'How do I connect my YouTube channel?', answer: 'Go to Settings → Integrations, click "Connect" next to YouTube, and complete the OAuth flow. You will need a Google Cloud Console project with the YouTube Data API v3 enabled. See the Platform Integration Guide for full instructions.' },
    { category: 'platforms', question: 'Is my platform data secure?', answer: 'Absolutely. We use OAuth 2.0 so you never share your password. Access tokens are stored AES-256 encrypted. All data is transmitted over TLS 1.3. We are SOC 2 Type II compliant and GDPR-ready.' },
    { category: 'platforms', question: 'Why is my connection showing as expired?', answer: 'Platform OAuth tokens expire (typically every 1–60 days). OmniConnX refreshes them automatically, but if the user revoked access on the platform side the token cannot be refreshed. Go to Settings → Integrations and click the edit icon → Refresh Token, or disconnect and reconnect.' },
    { category: 'analytics', question: 'How often are analytics updated?', answer: 'Automatic pulls run every 6 hours per platform. You can also trigger a manual refresh from the Analytics dashboard at any time (up to 10 refreshes per hour per platform). Real-time updates arrive via WebSocket when background jobs complete.' },
    { category: 'analytics', question: 'What is anomaly detection?', answer: 'Our AI anomaly detection runs Z-Score, IQR, and moving-average algorithms on your metrics history. It compares current values against your personal baseline (last 90 days) and fires an alert if a metric deviates by more than 2.5 standard deviations.' },
    { category: 'analytics', question: 'What does a SHADOW_BAN alert mean?', answer: 'A shadow-ban alert fires when your reach drops significantly (>40% vs your 7-day average) while impressions remain stable, indicating your content may be suppressed from discovery. This typically resolves in 24–72 hours without action.' },
    { category: 'billing', question: 'Can I cancel my subscription anytime?', answer: 'Yes, cancel anytime with no fees. Your subscription stays active until the end of your billing cycle. On cancellation, your data is retained for 90 days so you can export it before it is permanently deleted.' },
    { category: 'billing', question: 'What payment methods do you accept?', answer: 'We accept Visa, Mastercard, American Express, and PayPal via Stripe. Annual plans offer a 20% discount. Enterprise customers can pay by invoice (NET-30 terms).' },
    { category: 'troubleshooting', question: "Why aren't my metrics updating?", answer: 'Check Settings → Integrations for connection status. A red badge means the token has expired — reconnect the platform. If the status is green but metrics are stale, click Refresh in the Analytics dashboard. If the issue persists, contact support with your user ID.' },
    { category: 'troubleshooting', question: 'I forgot my password. What should I do?', answer: "Click \"Forgot Password\" on the login page and enter your email. You will receive a reset link within 2 minutes. The link expires after 15 minutes. If you don't see the email, check your spam folder or contact support." },
  ];

  // Icon tiles follow the OmniConnX logo ramp (orange → pink → violet → blue)
  const guides = [
    { title: 'Quick Start Guide', description: 'Get up and running with OmniConnX in 5 minutes', icon: RocketLaunchIcon, iconGradient: 'linear-gradient(135deg, #fb923c, #ec4899)', readTime: '5 min read' },
    { title: 'Platform Integration Guide', description: 'OAuth setup for YouTube, TikTok, Instagram, and Twitter', icon: LinkIcon, iconGradient: 'linear-gradient(135deg, #ec4899, #c026d3)', readTime: '10 min read' },
    { title: 'Analytics Dashboard Overview', description: 'Understand metrics, charts, filters, and anomaly detection', icon: ChartBarIcon, iconGradient: 'linear-gradient(135deg, #d946ef, #7c3aed)', readTime: '8 min read' },
    { title: 'Content Analytics', description: 'Per-post performance, formats, ranges, and the Published column', icon: PlayCircleIcon, iconGradient: 'linear-gradient(135deg, #f43f5e, #ec4899)', readTime: '7 min read' },
    { title: 'Publishing Content', description: 'Create and publish across platforms, with per-platform status', icon: DocumentTextIcon, iconGradient: 'linear-gradient(135deg, #06b6d4, #3b82f6)', readTime: '6 min read' },
    { title: 'Revenue Reconciliation', description: 'Track earnings and auto-reconcile with Stripe', icon: CurrencyDollarIcon, iconGradient: 'linear-gradient(135deg, #8b5cf6, #3b82f6)', readTime: '12 min read' },
  ];

  const filteredFaqs = faqs.filter((faq) => {
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    const matchesSearch = !searchQuery ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const filteredVideos = videoCategory === 'all'
    ? VIDEO_TUTORIALS
    : VIDEO_TUTORIALS.filter(v => v.category === videoCategory);

  const activeDoc = DOC_TABS.find(t => t.id === activeDocTab);
  const levelColor = { Beginner: 'text-green-600 bg-green-50 dark:bg-green-900/20', Intermediate: 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20', Advanced: 'text-red-600 bg-red-50 dark:bg-red-900/20' };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-950">
      {/* Nav */}
      <nav className="bg-white dark:bg-dark-900 border-b border-gray-200 dark:border-dark-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <BrandLogo size="md" />
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">Home</Link>
              <Link to="/login" className="btn btn-secondary">Login</Link>
              <Link to="/register" className="btn text-white bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 shadow-md">Sign Up</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden py-20">
        {/* Brand gradient — matches the OmniConnX logo (orange → pink → violet → blue) */}
        <div className="absolute inset-0" style={{ background: BRAND_HERO_GRADIENT }} />
        <div
          className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            How Can We Help You?
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="text-xl text-white/80 max-w-3xl mx-auto mb-8"
          >
            Search our knowledge base, read detailed guides, or watch video tutorials
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="max-w-2xl mx-auto"
          >
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400" />
              <input
                type="text"
                placeholder="Search guides, FAQs, documentation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-12 py-4 text-lg w-full"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-12 bg-white dark:bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a
              href="#documentation"
              className="card p-6 flex items-center gap-4 hover:shadow-lg transition-shadow group"
            >
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-105 transition-transform"
                style={{ background: 'linear-gradient(135deg, #60a5fa, #2563eb)' }}
              >
                <BookOpenIcon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Documentation</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Read detailed docs, API reference & webhooks</p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-400 ml-auto" />
            </a>

            <a
              href="#video-tutorials"
              className="card p-6 flex items-center gap-4 hover:shadow-lg transition-shadow group"
            >
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-105 transition-transform"
                style={{ background: 'linear-gradient(135deg, #f87171, #dc2626)' }}
              >
                <VideoCameraIcon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Video Tutorials</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Watch step-by-step walkthroughs</p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-400 ml-auto" />
            </a>

            <Link
              to="/contact"
              className="card p-6 flex items-center gap-4 hover:shadow-lg transition-shadow group"
            >
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-105 transition-transform"
                style={{ background: 'linear-gradient(135deg, #4ade80, #16a34a)' }}
              >
                <ChatBubbleLeftRightIcon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Contact Support</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Get help from our team · replies in &lt;4 hrs</p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-400 ml-auto" />
            </Link>
          </div>
        </div>
      </section>

      {/* Popular Guides */}
      <section className="py-20 bg-gray-100 dark:bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">Popular Guides</h2>
            <p className="text-gray-600 dark:text-gray-400">Click any guide to read the full walkthrough</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {guides.map((guide, index) => {
              const GuideIcon = guide.icon;
              return (
                <motion.button
                  key={index}
                  onClick={() => setActiveGuide(guide)}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card p-6 text-left hover:shadow-lg transition-all hover:-translate-y-1 group"
                >
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 shadow-sm group-hover:scale-110 transition-transform"
                    style={{ background: guide.iconGradient }}
                  >
                    <GuideIcon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{guide.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{guide.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-violet-600 dark:text-violet-400 font-medium">{guide.readTime}</span>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      Read <ChevronRightIcon className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Documentation */}
      <section id="documentation" className="py-20 bg-white dark:bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/20 rounded-full text-blue-600 dark:text-blue-400 font-semibold text-sm mb-4">
              <DocumentTextIcon className="w-4 h-4" />
              Developer Documentation
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">Read the Docs</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Complete reference for the OmniConnX API, authentication, webhooks, and rate limits.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar tabs */}
            <div className="lg:w-56 flex-shrink-0">
              <div className="card p-2 space-y-1">
                {DOC_TABS.map((tab) => {
                  const TabIcon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveDocTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors text-sm font-medium ${
                        activeDocTab === tab.id
                          ? 'bg-gradient-to-r from-pink-500 to-violet-600 text-white shadow-md'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-800'
                      }`}
                    >
                      <TabIcon className="w-4 h-4 flex-shrink-0" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 card p-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeDocTab}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-8"
                >
                  {activeDoc?.content.map((block, i) => (
                    <div key={i}>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{block.heading}</h3>
                      {block.body && (
                        <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-3">{block.body}</p>
                      )}
                      {block.code && (
                        <pre className="bg-gray-900 text-green-400 rounded-lg p-4 text-sm font-mono overflow-x-auto mb-3 whitespace-pre-wrap">
                          {block.code}
                        </pre>
                      )}
                      {block.list && (
                        <ul className="space-y-2">
                          {block.list.map((item, j) => (
                            <li key={j} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                              <ChevronRightIcon className="w-4 h-4 text-fuchsia-500 flex-shrink-0 mt-0.5" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* Video Tutorials */}
      <section id="video-tutorials" className="py-20 bg-gray-100 dark:bg-dark-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900/20 rounded-full text-red-600 dark:text-red-400 font-semibold text-sm mb-4">
              <PlayCircleIcon className="w-4 h-4" />
              Video Library
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">Video Tutorials</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Step-by-step video walkthroughs for every feature. New videos added weekly.
            </p>
          </div>

          {/* Video category filters */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {[
              { id: 'all', label: 'All Videos' },
              { id: 'getting-started', label: 'Getting Started' },
              { id: 'platforms', label: 'Platforms' },
              { id: 'analytics', label: 'Analytics' },
              { id: 'billing', label: 'Revenue & Billing' },
            ].map((cat) => (
              <button
                key={cat.id}
                onClick={() => setVideoCategory(cat.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  videoCategory === cat.id
                    ? 'bg-gradient-to-r from-pink-500 to-violet-600 text-white shadow-md'
                    : 'bg-white dark:bg-dark-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVideos.map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.07 }}
                className="card overflow-hidden group cursor-pointer hover:shadow-lg transition-shadow"
              >
                {/* Thumbnail */}
                <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 h-44 flex items-center justify-center">
                  <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <PlayCircleIcon className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded font-mono">
                    {video.duration}
                  </div>
                  <div className={`absolute top-3 left-3 text-xs px-2 py-1 rounded font-medium ${levelColor[video.level]}`}>
                    {video.level}
                  </div>
                </div>

                {/* Info */}
                <div className="p-5">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">
                    {video.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    {video.description}
                  </p>
                  <div className="mt-4 flex items-center gap-2 text-sm text-violet-600 dark:text-violet-400 font-medium">
                    <PlayCircleIcon className="w-4 h-4" />
                    Watch tutorial
                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 ml-auto text-gray-400" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white dark:bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3 text-center">
            Frequently Asked Questions
          </h2>
          <p className="text-center text-gray-600 dark:text-gray-400 mb-12">
            {filteredFaqs.length} result{filteredFaqs.length !== 1 ? 's' : ''}
            {searchQuery ? ` for "${searchQuery}"` : ''}
          </p>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {categories.map((category) => {
              const CatIcon = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r from-pink-500 to-violet-600 text-white shadow-md'
                      : 'bg-gray-100 dark:bg-dark-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-700'
                  }`}
                >
                  <CatIcon className="w-4 h-4" />
                  {category.name}
                </button>
              );
            })}
          </div>

          {/* FAQ accordion */}
          <div className="max-w-4xl mx-auto space-y-3">
            {filteredFaqs.length === 0 ? (
              <div className="card p-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-50 to-violet-50 dark:from-pink-900/20 dark:to-violet-900/20 flex items-center justify-center mx-auto mb-4">
                  <QuestionMarkCircleIcon className="w-8 h-8 text-violet-400" />
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  No results found. Try a different search or category.
                </p>
              </div>
            ) : (
              filteredFaqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.04 }}
                  className="card overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <span className="font-semibold text-gray-900 dark:text-white pr-4">{faq.question}</span>
                    <ChevronDownIcon
                      className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${openFaqIndex === index ? 'rotate-180' : ''}`}
                    />
                  </button>
                  <AnimatePresence>
                    {openFaqIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <p className="px-6 pb-6 text-gray-600 dark:text-gray-400 text-sm leading-relaxed border-t border-gray-100 dark:border-dark-800 pt-4">
                          {faq.answer}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Still Need Help CTA */}
      <section className="relative overflow-hidden py-20">
        {/* Brand gradient — matches the OmniConnX logo (orange → pink → violet → blue) */}
        <div className="absolute inset-0" style={{ background: BRAND_HERO_GRADIENT }} />
        <div
          className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }}
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Still Need Help?</h2>
          <p className="text-xl text-white/80 mb-8">Our support team responds within 4 hours on business days</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn bg-white text-violet-700 hover:bg-violet-50 shadow-md">
              Contact Support
            </Link>
            <Link to="/how-it-works" className="btn bg-violet-950/60 text-white hover:bg-violet-950/80 ring-1 ring-white/40">
              Read How It Works
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white dark:bg-dark-900 border-t border-gray-200 dark:border-dark-700 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-600 dark:text-gray-400">
          <p>&copy; 2026 OmniConnX. All rights reserved.</p>
        </div>
      </footer>

      {/* Guide Modal */}
      <AnimatePresence>
        {activeGuide && (
          <GuideModal guide={activeGuide} onClose={() => setActiveGuide(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default HelpPage;
